<template>
  <div class="resize-size">
    <div class="resize-size__header">
      <label
        v-if="label"
        class="resize-size__label"
      >{{ label }}</label>

      <div
        v-click-outside="closeDropdown"
        class="resize-size__preset-wrap"
      >
        <button
          type="button"
          class="resize-size__preset-trigger"
          @click="presetDropdownOpen = !presetDropdownOpen"
        >
          Select preset
          <b-icon
            :icon="presetDropdownOpen ? 'chevron-up' : 'chevron-down'"
            class="resize-size__preset-chevron"
          />
        </button>

        <div
          v-if="presetDropdownOpen"
          class="resize-size__preset-dropdown"
        >
          <button
            v-for="(preset) in presets"
            :key="`${preset.width}x${preset.height}`"
            type="button"
            class="resize-size__preset-option"
            :class="{ 'resize-size__preset-option--selected': isPresetSelected(preset) }"
            @click="selectPreset(preset)"
          >
            <b-icon
              v-if="isPresetSelected(preset)"
              icon="check"
              class="resize-size__preset-check"
            />
            <span :class="{ 'resize-size__preset-option-label--offset': !isPresetSelected(preset) }">
              {{ preset.width }}×{{ preset.height }} px
            </span>
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="value.length"
      class="resize-size__chips"
    >
      <div
        v-for="(size, i) in value"
        :key="size.id"
        class="resize-size__chip"
      >
        <span class="resize-size__chip-label">{{ size.width }} × {{ size.height }} px</span>
        <button
          type="button"
          class="resize-size__chip-remove"
          :aria-label="`Remove ${size.width} × ${size.height}`"
          @click="remove(i)"
        >
          <b-icon icon="x" />
        </button>
      </div>
    </div>

    <div class="resize-size__input-row">
      <div class="resize-size__size-input">
        <input
          v-model.number="width"
          type="number"
          placeholder="W"
          min="1"
          class="resize-size__size-field"
          @keydown.enter="add"
        >
        <span class="resize-size__size-sep">×</span>
        <input
          v-model.number="height"
          type="number"
          placeholder="H"
          min="1"
          class="resize-size__size-field"
          @keydown.enter="add"
        >
        <span class="resize-size__size-unit">px</span>
      </div>

      <b-button
        variant="link"
        size="sm"
        class="resize-size__add-btn"
        :disabled="!width || !height"
        @click="add"
      >
        <b-icon
          icon="plus"
          class="mr-1"
        />
        Add size
      </b-button>
    </div>
  </div>
</template>

<script>
import ClickOutside from 'vue-click-outside';
import { v4 as uuidv4 } from 'uuid';

export default {
  name: 'ResizeSizeInput',
  directives: { ClickOutside },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      width: null,
      height: null,
      presetDropdownOpen: false,
      presets: [
        { width: 300, height: 250 },
        { width: 728, height: 90 },
        { width: 320, height: 50 },
        { width: 160, height: 600 },
        { width: 300, height: 600 },
        { width: 336, height: 280 },
        { width: 970, height: 600 },
        { width: 970, height: 300 },
        { width: 1500, height: 1500 },
        { width: 2000, height: 2000 },
        { width: 2500, height: 2500 },
        { width: 3000, height: 3000 },
      ],
    }
  },
  methods: {
    add() {
      if (!this.width || !this.height) return;

      const w = Number(this.width);
      const h = Number(this.height);

      const exists = this.value.some(
        item => Number(item.width) === w && Number(item.height) === h
      );

      if (exists) return;

      this.$emit('input', [
        ...this.value,
        { id: uuidv4(), width: w, height: h }
      ]);

      this.width = null;
      this.height = null;
    },
    remove(i) {
      const updated = [...this.value];
      updated.splice(i, 1);
      this.$emit('input', updated);
    },
    isPresetSelected(preset) {
      return this.value.some(s => s.width === preset.width && s.height === preset.height);
    },
    selectPreset(preset) {
      if (!this.isPresetSelected(preset)) {
        this.$emit('input', [
          ...this.value,
          { id: uuidv4(), width: preset.width, height: preset.height }
        ]);
      } else {
        this.$emit('input', this.value.filter(
          s => !(s.width === preset.width && s.height === preset.height)
        ));
      }
    },
    closeDropdown() {
      this.presetDropdownOpen = false;
    },
  }
}
</script>

<style scoped lang="scss">
.resize-size {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #64748b;
  }

  &__preset-wrap {
    position: relative;
    display: inline-flex;
    align-items: center;
  }

  &__preset-trigger {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #209C86;
    border: none;
    border-radius: 8px;
    padding: 6px 10px 6px 14px;
    font-size: 12px;
    font-weight: 600;
    color: #fff;
    cursor: pointer;
    outline: none;
    transition: background 0.15s, box-shadow 0.15s;
    white-space: nowrap;

    &:hover {
      background: #1a8a76;
    }

    &:focus {
      box-shadow: 0 0 0 3px rgba(32, 156, 134, 0.35);
    }
  }

  &__preset-chevron {
    font-size: 14px !important;
    color: #FFF;
    flex-shrink: 0;
    display: flex !important;
    align-items: center;
  }

  &__preset-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    z-index: 100;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.08);
    overflow-y: auto;
    max-height: 152px;
    min-width: 160px;
  }

  &__preset-option {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 12px;
    background: none;
    border: 0;
    font-size: 12px;
    font-weight: 500;
    color: #475569;
    cursor: pointer;
    text-align: left;
    transition: background 0.1s;

    &:hover {
      background: #f8fafc;
    }

    &--selected {
      color: #059669;
      background: #f0fdf4;
    }
  }

  &__preset-check {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    color: #059669;
  }

  &__preset-option-label--offset {
    margin-left: 20px;
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 16px;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    padding: 4px 4px 4px 12px;
    background: #209C86;
    border: 1px solid #e2e8f0;
    border-radius: 9999px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }

  &__chip-label {
    font-size: 12px;
    font-weight: 600;
    color: #fff;
  }

  &__chip-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    margin-left: 6px;
    padding: 0;
    background: none;
    border: 0;
    border-radius: 9999px;
    color: #fff;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;

    &:hover {
      background: #fef2f2;
      color: #000;
    }
  }

  &__input-row {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  &__size-input {
    display: inline-flex;
    align-items: stretch;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
    transition: border-color 0.15s, box-shadow 0.15s;

    &:focus-within {
      border-color: #10b981;
      box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
    }
  }

  &__size-field {
    width: 56px;
    padding: 6px 10px;
    border: 0;
    background: transparent;
    text-align: center;
    font-size: 13px;
    color: #1f2937;
    outline: none;

    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    & + .resize-size__size-sep,
    & + .resize-size__size-unit {
      border-left: 1px solid #f1f5f9;
      border-right: 1px solid #f1f5f9;
    }
  }

  &__size-sep {
    display: inline-flex;
    align-items: center;
    padding: 0 8px;
    font-size: 12px;
    font-weight: 700;
    color: #cbd5e1;
  }

  &__size-unit {
    display: inline-flex;
    align-items: center;
    padding: 0 12px;
    background: #f8fafc;
    border-left: 1px solid #f1f5f9;
    font-size: 12px;
    font-weight: 600;
    color: #94a3b8;
  }

  &__add-btn {
    display: inline-flex !important;
    align-items: center;
    padding: 6px 12px !important;
    font-size: 14px;
    font-weight: 600;
    color: #059669 !important;
    text-decoration: none !important;
    border-radius: 8px;
    transition: background 0.15s, color 0.15s;

    &:hover:not(:disabled) {
      color: #047857 !important;
      background: #ecfdf5;
    }

    &:disabled {
      opacity: 0.4;
      background: transparent !important;
    }
  }
}
</style>
